<!-- Chat Agent Container -->
<div class="chat-agent-container">
  <!-- Agent Icon (always visible) -->
  <button class="agent-icon" [class.active]="isOpen" (click)="openChat($event)" title="Chat with our AI Assistant" type="button" aria-label="Open chat with AI Assistant">
    <img src="assets/icons/deo-agent-icon.svg" alt="DEO AI Assist" width="32" height="32" />
    <div class="pulse-ring" *ngIf="!isOpen"></div>
  </button>

  <!-- Chat Popup -->
  <div class="chat-popup" *ngIf="isOpen" (click)="stopPropagation($event)" (keydown)="stopPropagation($event)" tabindex="0">
    <!-- Close Button in Top Right Corner -->
    <button class="close-button-top-right" (click)="closeChat($event)" (keydown.enter)="closeChat($event)" title="Close chat" type="button" aria-label="Close chat">
      <svg-icon src="assets/icons/close-icon.svg" aria-hidden="true"></svg-icon>
    </button>

    <!-- Chat Header with Title and Dropdown -->
    <div class="chat-header">
      <deo-talking-to-selector
        *ngIf="isTalkingToEnabled; else staticTitle"
        class="chat-talking-to"
        [targets]="talkingToTargets"
        [selectedId]="selectedTargetId"
        (selected$)="onTalkingToSelected($event)"
      ></deo-talking-to-selector>

      <ng-template #staticTitle>
        <div class="chat-title">DEO AI Assist</div>
      </ng-template>
    </div>
    <div class="chat-select-wrapper">
      <gains-select [customFormControl]="chatSelectControl" [items]="chatOptions" placeholder="New Chat"> </gains-select>
    </div>

    <!-- Chat Messages Area -->
    <div class="chat-messages" #messagesContainer (scroll)="onMessagesScroll()">
      <!-- Messages -->
      <div
        *ngFor="let message of messages; trackBy: trackByMessage"
        class="message-wrapper"
        [class.user-message]="message.role === 'user'"
        [class.agent-message]="message.role === 'assistant'"
      >
        <!-- Message Label with Timestamp -->
        <div class="message-label" *ngIf="message.role === 'assistant'">
          <!-- Agent Icon (only for agent messages) -->
          <div class="agent-icon-small">
            <img src="assets/icons/deo-agent-icon.svg" alt="DEO AI Assist" width="16" height="16" />
          </div>
        </div>

        <!-- Message Bubble -->
        <div class="message-bubble">
          <div class="message-header">
            <span class="label-text">{{ messageAuthor(message) }}</span>
            <span class="label-timestamp" *ngIf="message?.timestamp">{{ formatTime(message.timestamp) }}</span>
          </div>
          <!-- The agent's notes and lookups, reported before the answer (DATA-1629) -->
          <deo-message-progress
            *ngIf="message.progressSteps?.length"
            [steps]="message.progressSteps!"
            [collapsed]="isProgressCollapsed(message)"
            (toggled$)="toggleProgress(message)"
          ></deo-message-progress>

          <!-- Typing dots: shown only during heartbeat phase (streaming active, no content yet) -->
          <div class="typing-indicator" *ngIf="message.isTyping && !message.content">
            <span class="typing-text">DEO is thinking</span>
            <div class="typing-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>

          <!-- Message content: shown once content is available (streaming or complete) -->
          <div class="message-text" *ngIf="message.content">
            <!-- The caret would keep blinking at the end of a finished answer during the artifact phase; the activity line reports that instead. -->
            <div [class.streaming-cursor]="message.isStreaming && !message.activity" [innerHTML]="formatMessageContent(getDisplayedContent(message))"></div>
          </div>

          <!-- Shown while the backend reports work after the answer text, e.g. building the visualization -->
          <div class="activity-indicator" *ngIf="message.activity" role="status" aria-live="polite">
            <span class="activity-text">{{ getActivityLabel(message) }}</span>
            <div class="typing-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>

          <!-- Follow-up prompts an agent offered with its answer (PLAN-2368, own flag) -->
          <deo-conversation-chips
            *ngIf="followUpsFor(message).length"
            [prompts]="followUpsFor(message)"
            [disabled]="isResponseInProgress"
            [label]="'Follow-up questions for ' + message.agentReply?.agentName"
            (chosen$)="onFollowUpChosen($event)"
          ></deo-conversation-chips>

          <deo-conversation-chips
            *ngIf="isLastMessage(message) && clarificationOptions.length"
            [prompts]="clarificationOptions"
            [disabled]="isResponseInProgress"
            label="Suggested replies"
            (chosen$)="onFollowUpChosen($event)"
          ></deo-conversation-chips>

          <!-- Attachment in message -->
          <div class="message-attachment-chip" *ngIf="message.attachment && !message.isTyping">
            <svg-icon [src]="getFileIcon(message.attachment.name)" class="file-type-icon"></svg-icon>
            <span class="file-name">{{ message.attachment.name }}</span>
            <span class="file-size">{{ formatFileSize(message.attachment.size) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Chat Input Area -->
    <div class="chat-input-container">
      <div class="input-field-wrapper" [ngClass]="{ 'input-field-wrapper-disabled': isLoading || isTyping }">
        <div class="textarea-wrapper">
          <!-- Selected file chip -->
          <div class="selected-file-chip" *ngIf="selectedFile">
            <svg-icon [src]="getFileIcon(selectedFile.name)" class="file-type-icon"></svg-icon>
            <span class="file-name">{{ selectedFile.name }}</span>
            <span class="file-size">{{ formatFileSize(selectedFile.size) }}</span>
            <button class="remove-btn" aria-label="Remove attached file" title="Remove attached file" (click)="removeFile()" type="button">
              <svg-icon src="assets/icons/close-icon.svg" aria-hidden="true"></svg-icon>
            </button>
          </div>

          <textarea
            id="widget-message-input"
            aria-label="Message DEO"
            class="chat-textarea"
            [placeholder]="composerPlaceholder"
            [ngModel]="currentMessage"
            (ngModelChange)="onMessageChange($event)"
            (keydown)="onKeyDown($event)"
            rows="2"
            [disabled]="isTyping || isLoading"
          ></textarea>
          <div class="button-row" [ngClass]="{ 'button-row-disabled': isLoading || isTyping }">
            <div class="left-actions">
              <nz-upload *ngIf="isAttachFileEnabled" [nzBeforeUpload]="beforeUpload" [nzShowUploadList]="false" nzAccept=".txt,.pdf,.csv,.md" [nzDisabled]="isTyping || isLoading">
                <button class="attach-btn" nz-button type="button" [disabled]="isTyping || isLoading" title="Attach file">
                  <svg-icon src="assets/icons/attachment.svg"></svg-icon>
                  <span>Attach file</span>
                </button>
              </nz-upload>

              <button
                *ngIf="isLiveTranscriptionEnabled"
                class="dictate-btn"
                [class.listening]="isListening"
                [disabled]="isTyping || isLoading"
                (click)="toggleVoiceInput()"
                [title]="isListening ? 'Stop recording' : 'Start voice input'"
                [attr.aria-label]="isListening ? 'Stop recording' : 'Start voice input'"
                type="button"
              >
                <svg-icon src="assets/icons/microphone.svg"></svg-icon>
                <span class="dictate-text">{{ isListening ? 'Listening...' : 'Dictate' }}</span>
              </button>
            </div>

            <!-- Stop button replaces Send only while a response is streaming and the feature flag is on -->
            <button *ngIf="canStopResponse" class="send-btn stop-btn" type="button" (click)="onStopStreaming()" aria-label="Stop response" title="Stop response">
              <svg-icon src="assets/icons/stop.svg"></svg-icon>
            </button>
            <button *ngIf="!canStopResponse" class="send-btn" type="button" [disabled]="isSendDisabled" (click)="sendMessage()" aria-label="Send message">
              <svg-icon src="assets/icons/send.svg"></svg-icon>
            </button>
          </div>
        </div>
      </div>
      <div class="error-message" *ngIf="voiceError">
        <span class="error-text">{{ voiceError }}</span>
      </div>
    </div>
  </div>
</div>
